import React, { useEffect, useState } from "react";
import { taskDetailApi } from "../../backend/client";
import {
  TaskDialog,
  TaskDialogActions,
  TaskMessage,
  TaskTextArea,
  TaskTextInput,
} from "./taskFormUi";

const emptyForm = {
  building_name: "",
  contact_person: "",
  contact_phone: "",
  booking_date: "",
  booking_time: "",
  access_instructions: "",
  notes: "",
};

export default function ElevatorAccessForm({
  isOpen,
  onClose,
  projectId,
  taskId,
  onSave,
}: any) {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const update = (key, value) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  useEffect(() => {
    if (!isOpen || !projectId) return;

    (async () => {
      setLoading(true);
      setErrorMessage("");
      const { data, error } = await taskDetailApi.list("elevator-access", {
        project_id: projectId,
        ...(taskId ? { task_id: taskId } : {}),
        order: "updated_at",
        ascending: false,
        limit: 1,
      });

      if (!error && data?.[0]) {
        const row = data[0];
        setForm({
          building_name: row.building_name ?? "",
          contact_person: row.contact_person ?? "",
          contact_phone: row.contact_phone ?? "",
          booking_date: row.booking_date ?? "",
          booking_time: row.booking_time ?? "",
          access_instructions: row.access_instructions ?? "",
          notes: row.notes ?? "",
        });
      } else {
        setForm(emptyForm);
      }
      setLoading(false);
    })();
  }, [isOpen, projectId, taskId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage("");

    const payload = {
      project_id: projectId,
      task_id: taskId ?? null,
      building_name: form.building_name || null,
      contact_person: form.contact_person || null,
      contact_phone: form.contact_phone || null,
      booking_date: form.booking_date || null,
      booking_time: form.booking_time || null,
      access_instructions: form.access_instructions || null,
      notes: form.notes || null,
    };

    const { error } = await taskDetailApi.upsert("elevator-access", payload);
    setLoading(false);

    if (error) {
      setErrorMessage(error.message || "Failed to save elevator access");
      return;
    }

    onSave?.();
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <TaskDialog
      title="Book Elevator Access"
      description="Building contact, access timing, and move-day instructions."
      onClose={onClose}
      className="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <TaskMessage>{errorMessage}</TaskMessage>

        <div className="grid gap-4 sm:grid-cols-2">
          <TaskTextInput
            id="elevator-building-name"
            label="Building Name"
            value={form.building_name}
            onChange={(event) => update("building_name", event.target.value)}
            required
            className="sm:col-span-2"
          />
          <TaskTextInput
            id="elevator-contact-person"
            label="Contact Person"
            value={form.contact_person}
            onChange={(event) => update("contact_person", event.target.value)}
          />
          <TaskTextInput
            id="elevator-contact-phone"
            label="Phone"
            value={form.contact_phone}
            onChange={(event) => update("contact_phone", event.target.value)}
          />
          <TaskTextInput
            id="elevator-booking-date"
            label="Booking Date"
            type="date"
            value={form.booking_date}
            onChange={(event) => update("booking_date", event.target.value)}
          />
          <TaskTextInput
            id="elevator-booking-time"
            label="Booking Time"
            type="time"
            value={form.booking_time}
            onChange={(event) => update("booking_time", event.target.value)}
          />
          <TaskTextArea
            id="elevator-access-instructions"
            label="Access Instructions"
            value={form.access_instructions}
            onChange={(event) =>
              update("access_instructions", event.target.value)
            }
            rows={3}
            className="sm:col-span-2"
          />
          <TaskTextArea
            id="elevator-notes"
            label="Notes"
            value={form.notes}
            onChange={(event) => update("notes", event.target.value)}
            rows={3}
            className="sm:col-span-2"
          />
        </div>

        <TaskDialogActions
          onCancel={onClose}
          submitType="submit"
          disabled={loading}
          submitLabel={loading ? "Saving..." : "Save Access"}
        />
      </form>
    </TaskDialog>
  );
}
